'use client';

import { useState } from 'react';
import { AlertTriangle, ChevronDown, ChevronUp, RefreshCw } from 'lucide-react';
import type { ParsedAdminApiError } from '@/lib/admin-api-error';
import { AdminButton } from '@/components/ui/admin/AdminButton';

type Props = {
  error: ParsedAdminApiError;
  onRetry?: () => void;
  className?: string;
};

export function AdminApiErrorBanner({ error, onRetry, className = '' }: Props) {
  const [showTechnical, setShowTechnical] = useState(false);

  return (
    <div className={`rounded-xl border border-mist/60 bg-mist/30 p-4 text-sm text-soot ${className}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-2 min-w-0">
          <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold">{error.message}</p>
            {error.requestId ? (
              <p className="text-xs mt-1 font-mono text-soot">Request ID: {error.requestId}</p>
            ) : null}
          </div>
        </div>
        <div className="flex gap-2 shrink-0">
          {onRetry ? (
            <AdminButton variant="outline" size="sm" icon={RefreshCw} onClick={onRetry}>
              Retry
            </AdminButton>
          ) : null}
          <button
            type="button"
            className="text-xs font-bold uppercase tracking-wider text-soot flex items-center gap-1"
            onClick={() => setShowTechnical((v) => !v)}
          >
            Technical details
            {showTechnical ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>
        </div>
      </div>
      {showTechnical ? (
        <dl className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs font-mono bg-bg/60 rounded-lg p-3 border border-mist/60">
          <div>
            <dt className="text-soot">Error code</dt>
            <dd>{error.errorCode ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-soot">HTTP status</dt>
            <dd>{error.status ?? '—'}</dd>
          </div>
          <div className="sm:col-span-2">
            <dt className="text-soot">API endpoint</dt>
            <dd className="break-all">{error.endpoint ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-soot">Timestamp</dt>
            <dd>{error.timestamp}</dd>
          </div>
          <div>
            <dt className="text-soot">X-Request-ID</dt>
            <dd>{error.requestId ?? '—'}</dd>
          </div>
        </dl>
      ) : null}
    </div>
  );
}
